/**
 * Help articles on the web (CLA-1838): the article, the index the Help page
 * lists, and "Was this helpful?". Server: claro-elixir `HelpController`.
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { components } from '@/api/schema'
import { searchHelp } from '@/api/search'

export type HelpArticle = components['schemas']['HelpArticle']
export type HelpIndexArticle = components['schemas']['HelpIndexArticle']
type HelpIndexResponse = components['schemas']['HelpIndexResponse']
type HelpFeedbackResponse = components['schemas']['HelpFeedbackResponse']
export type HelpSearchResult = components['schemas']['HelpSearchResult']

export function helpArticlePath(slug: string): string {
  return `/help/${encodeURIComponent(slug)}`
}

export function useHelpArticle(slug: string) {
  return useQuery({
    queryKey: ['help', 'article', slug],
    queryFn: () => api.get<HelpArticle>(`/help/articles/${encodeURIComponent(slug)}`),
    enabled: slug !== '',
  })
}

/** Every article for this user's role, without bodies. Changes only on deploy. */
export function useHelpIndex(enabled = true) {
  return useQuery({
    queryKey: ['help', 'index'],
    queryFn: () => api.get<HelpIndexResponse>('/help/articles').then((r) => r.articles),
    staleTime: 10 * 60 * 1000,
    enabled,
  })
}

/** How long typing must pause before the full-text search is asked. */
export const HELP_FULL_TEXT_DEBOUNCE_MS = 300

/**
 * Full-text matches, bodies included, from the server (`POST /help/search`,
 * the query in the body, never the URL). It complements `matchHelpIndex`,
 * which answers instantly from the index: ask it once typing pauses, and
 * only for three characters or more. An older server (no POST route) or any
 * error leaves `data` empty, so the instant matches still stand.
 */
export function useHelpFullText(query: string, { enabled = true, limit = 5 } = {}) {
  const q = query.trim()
  const [settled, setSettled] = useState(q)
  useEffect(() => {
    const t = setTimeout(() => setSettled(q), HELP_FULL_TEXT_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [q])

  const asking = enabled && settled.length >= 3 && settled === q
  const result = useQuery({
    queryKey: ['help', 'search', settled, limit],
    queryFn: ({ signal }) => searchHelp({ q: settled, limit }, { signal }).then((r) => r.articles),
    enabled: asking,
    staleTime: 10 * 60 * 1000,
    retry: false,
  })
  return {
    data: asking && !result.isError ? (result.data ?? []) : [],
    /** True while an answer for what is typed now is still to come. */
    pending: enabled && q.length >= 3 && (q !== settled || result.isFetching),
  }
}

/** `first`, then each of `more` whose slug is not already listed, up to `limit`. */
export function mergeHelpMatches<A extends { slug: string }, B extends { slug: string }>(
  first: readonly A[],
  more: readonly B[],
  limit: number,
): Array<A | B> {
  const seen = new Set(first.map((a) => a.slug))
  return [...first, ...more.filter((a) => !seen.has(a.slug))].slice(0, limit)
}

export function sendHelpFeedback(slug: string, helpful: boolean) {
  return api.post<HelpFeedbackResponse>(`/help/articles/${encodeURIComponent(slug)}/feedback`, {
    helpful,
  })
}

/** "clinical-workflows" → "Clinical workflows". */
export function categoryLabel(category: string): string {
  const words = category.replace(/[-_]+/g, ' ').trim()
  return words ? words[0].toUpperCase() + words.slice(1) : 'General'
}

// Question words that say nothing about which article is meant.
const STOP_WORDS = new Set([
  'how', 'what', 'where', 'when', 'why', 'who', 'which', 'can', 'could', 'does', 'the', 'and',
  'for', 'with', 'you', 'your', 'our', 'this', 'that', 'there', 'are', 'not', 'into', 'from',
])

/**
 * Index articles matching every word of `query` (three letters or more, not
 * a question word) in the title, keywords, question or answer, best first.
 * Runs in the browser, so what is typed (in ⌘K, often a patient's name)
 * never leaves it.
 */
export function matchHelpIndex(
  articles: readonly HelpIndexArticle[],
  query: string,
  limit = 3,
): HelpIndexArticle[] {
  const words = query.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((w) => w.length >= 3 && !STOP_WORDS.has(w))
  if (words.length === 0) return []
  const scored: Array<{ article: HelpIndexArticle; score: number }> = []
  for (const article of articles) {
    const title = article.title.toLowerCase()
    const rest = `${article.keywords.join(' ')} ${article.question} ${article.answer}`.toLowerCase()
    if (!words.every((w) => title.includes(w) || rest.includes(w))) continue
    scored.push({ article, score: words.filter((w) => title.includes(w)).length })
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.article)
}
